import { IfWrites } from '../../components/write-access';
import { useState, type ReactNode } from 'react';
import { Link, Outlet, useOutletContext, useNavigate, useParams } from 'react-router-dom';
import { TabLink } from '../../components/ui/scroll-reset';
import type { NeedsActionRenameProposal, OrganisationRecord } from '@ni/shared';
import {
  useAnswerRenameProposal,
  useNeedsAction,
  useOrganisation,
  useUntrackOrganisation,
} from '../../api/hooks';
import { hasCode, loadErrorLine } from '../../api/errors';
import {
  KNOWLEDGE_RENAME_ACTION,
  ORGANISATIONS_TITLE,
  ORGANISATION_SUBLINE_FALLBACK,
  ORGANISATION_TAB_LABELS,
  ORGANISATION_UNTRACK_ACTION,
  ORGANISATION_UNTRACK_KEEP,
  ORGANISATION_UNTRACK_WORKING,
  RAW_DATA_ACTION,
  RENAME_PROPOSAL_NO_WORKING,
  RENAME_PROPOSAL_YES,
  RENAME_PROPOSAL_YES_WORKING,
  organisationRenameProposalLine,
  organisationUntrackLine,
  renameProposalNo,
} from '../../lib/copy';
import { CLAUSE_SEPARATOR, joinClauses } from '../../lib/format';
import { organisationEverInvestigated, organisationRunHolds } from '../../lib/run-state';
import { RenameRecordModal } from '../../components/knowledge/rename-modal';
import { OrganisationLookupModal } from '../../components/organisations/lookup-modal';
import { ErrorLine } from '../../components/ui/error-line';
import { Modal } from '../../components/ui/modal';
import { SiteLink } from '../../components/ui/site-link';
import { WorkingButton } from '../../components/ui/working-button';
import { Loading } from '../../components/ui/loading';
import { EmptyLine } from '../../components/ui/empty-line';
import { Button, DangerLink } from '../../components/ui/button';
import { TopBar } from '../../components/ui/top-bar';
import { RecordHeader } from '../../components/record/record-header';
import { ResearchFooter } from '../../components/brief/research-footer';
import './organisation.css';

/**
 * **The organisation's record shell** (organisations-as-subjects note §1 R8, R9)
 * — the person record's own shell, over an organisation: a breadcrumb, the
 * restrained header, three tabs and the foot.
 *
 * It was one screen and no tabs until the owner read the first real one
 * (2026-09-09): "the network graph should be on a separate tab. The structure of
 * the page should be: Overview (contains lookup and brief, also structured info
 * like leadership); Network (the network graph, just like with people)". There
 * is still no research tab, an organisation having no research surface separate
 * from its brief — the lookup writes the summary the Overview opens with — but
 * there are three, People having been cut out of the Overview on the owner's
 * ruling of 2026-09-10 ("The organization Overview tab is now a bit large. Take
 * the leadership and investors sections and move them to a 'People' tab").
 *
 * What used to sit between them is gone with the same rulings: the office's own
 * statements, how the record stands to other organisations, and where all of it
 * came from. None of that was lost — the knowledge panel still reads all three —
 * but on the record they were three blocks a reader scrolled past to reach the
 * things this page is for.
 *
 * The shell keeps exactly what is true of the record rather than of a tab: the
 * name, the one door that changes it, the question the lookup can raise about
 * it, and the door that stops tracking it. Tabs are route-addressed, as a
 * person's are.
 */

/** What the shell hands down: the folded id, and the record the tabs read. */
export interface OrganisationRecordContext {
  /**
   * The organisation's entity id, **lowercased at the `useParams` boundary
   * below** and canonical from there down — the person record's own rule. Every
   * consumer reads it from here rather than calling `useParams` again, or the
   * raw and the folded spelling are back on the same page.
   */
  entityId: string;
  record: OrganisationRecord;
}

export function useOrganisationRecord(): OrganisationRecordContext {
  return useOutletContext<OrganisationRecordContext>();
}

/**
 * The line under the name: what the office has confirmed about the place, as
 * text. The website is a link on the page (see {@link OrganisationSubline}), and
 * this is still what the line *says* — the two are asserted against each other
 * rather than one being the other's markup.
 */
export function organisationSubline(subject: OrganisationRecord['subject']): string {
  return (
    joinClauses([subject.website, subject.location, subject.jurisdiction]) ||
    ORGANISATION_SUBLINE_FALLBACK
  );
}

/**
 * **The same line, with the website as a link out** (owner ruling, 2026-09-10).
 *
 * The header used to print the site as plain text, and said why: nothing on a
 * record page leaves the app, and a reader is not one mis-click from being
 * somewhere else. The office asked for the opposite, and this is it — the one
 * value on the record that is an address rather than a fact about the record.
 *
 * The clauses after it are text and stay text, joined by the same separator
 * `joinClauses` uses, so a reader sees one line rather than a link and a list.
 *
 * **The primary, where the record has confirmed several** (owner ruling,
 * 2026-09-10). `subject.website` is the earliest confirmed host and the head
 * prints that one alone: a header carrying two addresses would make the reader
 * decide which the company is at. The others are said in the About block, under
 * the row that shows this one.
 */
export function OrganisationSubline({ subject }: { subject: OrganisationRecord['subject'] }) {
  const rest = joinClauses([subject.location, subject.jurisdiction]);
  if (!subject.website) return <>{rest || ORGANISATION_SUBLINE_FALLBACK}</>;

  return (
    <>
      <SiteLink value={subject.website} />
      {rest ? `${CLAUSE_SEPARATOR}${rest}` : ''}
    </>
  );
}

/**
 * **When the name proposal has left the page** — the inbox card's own latch
 * (`mergeCardIsAnswered`), over the one question this record can be asked.
 *
 * Two ways in, and they are the same outcome: this reader answered, or somebody
 * else answered first and the POST came back `DECISION_ALREADY_ANSWERED`, which
 * on a settled question is not an error to report. Without the latch the pair
 * re-enables between the mutation settling and the inbox refetch landing — a
 * second or so in which Yes and No are live over a question that is already
 * answered, and pressing one asks the server to answer it twice.
 */
export function renameProposalIsAnswered(answered: boolean, error: unknown): boolean {
  return answered || hasCode(error, 'DECISION_ALREADY_ANSWERED');
}

/**
 * **The shell, given the record** — the split this route has always made: nothing
 * here fetches, so it is asserted as static markup, and the one thing that does
 * (the tab under it) arrives as a node the route mounts.
 */
export function OrganisationRecordShell({
  record,
  body,
  proposal,
  proposalWorking,
  proposalError,
  canRefresh,
  onProposal,
  onRefresh,
  onRename,
  onUntrack,
}: {
  record: OrganisationRecord;
  /** The tab below the nav — the route's `<Outlet>`. */
  body: ReactNode;
  /**
   * The lookup's name proposal for **this** record, or null.
   *
   * It comes off the needs-action projection rather than off `pending_decisions`,
   * and that is the contract's doing, not a shortcut: `DecisionSummary` excludes
   * `entity_rename` outright (`shared/src/decisions.ts`) — the record's pending
   * list is the review counter's, and a question about a name is not a finding
   * about anybody — so the row is only ever carried in `rename_proposals[]`.
   *
   * It lives on the **shell** rather than on a tab because it is a question
   * about the name in the header above it, which is on every tab.
   */
  proposal: NeedsActionRenameProposal | null;
  proposalWorking: 'rename' | 'keep' | null;
  proposalError: unknown;
  /**
   * **The record has been investigated and nothing is running** — the two
   * conditions Refresh stands on, decided by the page above out of the record's
   * own run list. Before an investigation there is a button at the top of the
   * Overview asking the question that comes next, and offering a repeat beside
   * it would put the office back in front of the choice the single-button rule
   * took away.
   */
  canRefresh: boolean;
  onProposal: (confirmed: boolean) => void;
  /**
   * Refresh: **opens the box**, which then runs the investigation again (owner
   * ruling, 2026-09-10: "Unlike the person equivalent, hitting refresh on the
   * org page has no confirmation box"). The press writes nothing, so the foot
   * holds no working state and no refusal — both belong to the box.
   */
  onRefresh: () => void;
  onRename: () => void;
  onUntrack: () => void;
}) {
  const { subject } = record;
  const sector = record.profile?.sector?.trim() || null;

  return (
    <>
      {/* Rename sits beside the title, in the bar (blend spec, "Quiet edit
          controls"); the record's other doors are in the foot. */}
      <TopBar
        parent={{ to: '/organizations', label: ORGANISATIONS_TITLE }}
        here={subject.display_name}
        actions={
          <IfWrites>
            <button type="button" className="quiet-action org-rename" onClick={onRename}>
              {KNOWLEDGE_RENAME_ACTION}
            </button>
          </IfWrites>
        }
      />

      {/* The key-facts line: the sector, then the website as a link out (owner
          ruling, 2026-09-10; the one value here that is an address, opening a
          tab of its own), then the place. */}
      <RecordHeader
        name={subject.display_name}
        facts={
          <div className="subline org-facts">
            {sector ? (
              <>
                <b>{sector}</b>
                {CLAUSE_SEPARATOR}
              </>
            ) : null}
            <OrganisationSubline subject={subject} />
          </div>
        }
      />

      {/* The lookup's name proposal, under the header whose name it questions.
          The same question is in the Home inbox, answered by the same hook. */}
      {proposal ? (
        <IfWrites>
          <div className="org-proposal">
            <p>{organisationRenameProposalLine(proposal.to_name)}</p>
            <div className="org-proposal-actions">
              <WorkingButton
                label={RENAME_PROPOSAL_YES}
                workingLabel={RENAME_PROPOSAL_YES_WORKING}
                variant="act"
                working={proposalWorking === 'rename'}
                disabled={proposalWorking !== null && proposalWorking !== 'rename'}
                onClick={() => onProposal(true)}
              />
              <WorkingButton
                label={renameProposalNo(proposal.from_name)}
                workingLabel={RENAME_PROPOSAL_NO_WORKING}
                variant="act"
                working={proposalWorking === 'keep'}
                disabled={proposalWorking !== null && proposalWorking !== 'keep'}
                onClick={() => onProposal(false)}
              />
            </div>
            {/* The refusal stays where the reader is looking and the question
                stays open behind it. */}
            {proposalError ? <ErrorLine error={proposalError} /> : null}
          </div>
        </IfWrites>
      ) : null}

      {/* Overview is the index route; People holds the leadership and investors
          (owner ruling, 2026-09-10); Network is the person tab's own drawing. */}
      <div className="tabs">
        <TabLink end to={`/organizations/${subject.entity_id}`}>
          {ORGANISATION_TAB_LABELS.overview}
        </TabLink>
        <TabLink to={`/organizations/${subject.entity_id}/people`}>
          {ORGANISATION_TAB_LABELS.people}
        </TabLink>
        <TabLink to={`/organizations/${subject.entity_id}/network`}>
          {ORGANISATION_TAB_LABELS.network}
        </TabLink>
      </div>

      <div className="tab-panel">{body}</div>

      {/* The person research tab's own foot: Refresh, then Raw data (a button
          in the foot, not a tab: owner ruling, 2026-09-10), then Stop tracking,
          last and set off by its hairline. Refresh opens the lookup's box at
          investigation depth rather than starting anything on the press. */}
      <ResearchFooter
        onRefresh={canRefresh ? onRefresh : undefined}
        rawData={
          <Link to={`/organizations/${subject.entity_id}/data`} className="foot-link">
            {RAW_DATA_ACTION}
          </Link>
        }
        deleteAction={<DangerLink onClick={onUntrack}>{ORGANISATION_UNTRACK_ACTION}</DangerLink>}
      />
    </>
  );
}

/**
 * **The organisation's record page.** It owns the record read and the two doors
 * that are about the record rather than about a tab; the shell above draws the
 * answer, and the tabs below it read what they need off the outlet context.
 */
export function OrganisationRecordPage() {
  /* **The one place an organisation's id enters the app from outside**, folded
     here for the reason the person record folds its own (`person/record.tsx`): a
     uuid's hex is case-insensitive, every layer below agrees, and the ids that
     come back out of Postgres are lower-case — so `/organisations/AB12…` is an
     ordinary 200 whose id matches nothing it is put beside. Folding also keeps
     one organisation to one cache entry, this segment being part of every query
     key under it. `NavLink` matching is case-insensitive by default, so the tabs
     still highlight on a loud URL. */
  const { orgEntityId: rawEntityId = '' } = useParams();
  const entityId = rawEntityId.toLowerCase();
  const record = useOrganisation(entityId);
  const navigate = useNavigate();

  const [renaming, setRenaming] = useState(false);
  const [untracking, setUntracking] = useState(false);
  /** Refresh's box, open or shut — the run behind it is the box's to create. */
  const [refreshing, setRefreshing] = useState(false);

  const untrack = useUntrackOrganisation(entityId);
  const answerProposal = useAnswerRenameProposal(entityId);

  /* **The id Refresh's box hands back**, held for the seconds before the record
     has been read again, exactly as the Overview tab holds its own: the create
     invalidates the record and the refetch is a round trip, in which the list
     still ends on the completed investigation and would offer Refresh over a run
     that is going.

     The create itself is the box's since 2026-09-10 — the foot no longer posts
     anything, so there is no mutation here at all. */
  const [startedRunId, setStartedRunId] = useState<string | null>(null);

  /* The proposal for this record, off the inbox projection — `pending_decisions`
     cannot carry one (`DecisionSummary` excludes `entity_rename` by contract).
     The read is the same query Home polls, so it is usually already in hand. */
  const needsAction = useNeedsAction();
  const proposal = renameProposalIsAnswered(answerProposal.isSuccess, answerProposal.error)
    ? null
    : (needsAction.data?.rename_proposals.find((row) => row.entity_id === entityId) ?? null);

  if (record.isPending) {
    return (
      <div className="column record-column">
        <EmptyLine>
          <Loading />
        </EmptyLine>
      </div>
    );
  }

  if (record.isError) {
    return (
      <div className="column record-column">
        <TopBar parent={{ to: '/organizations', label: ORGANISATIONS_TITLE }} />
        <EmptyLine>{loadErrorLine(record.error)}</EmptyLine>
      </div>
    );
  }

  const subject = record.data.subject;
  const runs = record.data.runs;
  /* The record polls no run of its own — the Overview tab does that, and its
     `useOrganisationRunStateInvalidation` is what brings this list back the
     moment a run's state moves. So the polled state is `null` here and the
     started id below carries the only window the list is wrong in. */
  const started = runs.some((run) => run.id === startedRunId) ? null : startedRunId;
  const canRefresh =
    organisationEverInvestigated(runs) && !organisationRunHolds(runs, null, started);

  return (
    <div className="column record-column">
      <OrganisationRecordShell
        record={record.data}
        body={
          <Outlet context={{ entityId, record: record.data } satisfies OrganisationRecordContext} />
        }
        proposal={proposal}
        proposalWorking={
          answerProposal.isPending
            ? answerProposal.variables?.confirmed
              ? 'rename'
              : 'keep'
            : null
        }
        proposalError={answerProposal.isError ? answerProposal.error : null}
        canRefresh={canRefresh}
        onRefresh={() => setRefreshing(true)}
        onProposal={(confirmed) => {
          if (!proposal) return;
          answerProposal.mutate({ decisionId: proposal.decision_id, confirmed });
        }}
        onRename={() => setRenaming(true)}
        onUntrack={() => setUntracking(true)}
      />

      {/* **Refresh's box** — the lookup's own, at investigation depth (owner
          ruling, 2026-09-10). It files what the office typed and then creates
          the run, in that order, so a correction typed here is on the record
          before the run freezes its context. The id comes back so the foot stops
          offering Refresh over a run that is now going; the strip itself mounts
          at the top of the Overview off the record's refreshed run list, exactly
          as it does for a lookup. */}
      {refreshing ? (
        <OrganisationLookupModal
          entityId={entityId}
          mode="investigation"
          /* The one door that is always a repeat: Refresh is offered in the foot
             under a reading that already happened, so its head says "again" and
             the Overview's Investigate does not. */
          again
          onClose={() => setRefreshing(false)}
          onStarted={(runId) => setStartedRunId(runId)}
        />
      ) : null}

      {/* The same modal the knowledge panel and the person header open — one
          door, one endpoint, one set of words. `useRenameKnowledge` invalidates
          this organisation's record and the roster it is listed on, so the
          header is already reading the new name by the time the modal closes. */}
      {renaming ? (
        <RenameRecordModal
          here={{ entity_id: entityId, display_name: subject.display_name }}
          onClose={() => setRenaming(false)}
        />
      ) : null}

      {/* The house confirm: one line saying what it does and — the half that
          matters here — what it does not. */}
      {untracking ? (
        <Modal label={ORGANISATION_UNTRACK_ACTION} narrow onClose={() => setUntracking(false)}>
          <div className="modal-head">{ORGANISATION_UNTRACK_ACTION}</div>
          <div className="modal-line">{organisationUntrackLine(subject.display_name)}</div>
          {untrack.isError ? <ErrorLine error={untrack.error} /> : null}
          <div className="modal-actions">
            <WorkingButton
              label={ORGANISATION_UNTRACK_ACTION}
              workingLabel={ORGANISATION_UNTRACK_WORKING}
              variant="danger"
              working={untrack.isPending}
              onClick={() =>
                untrack.mutate(undefined, {
                  onSuccess: () => {
                    setUntracking(false);
                    navigate('/organizations');
                  },
                })
              }
            />
            <Button variant="act" onClick={() => setUntracking(false)}>
              {ORGANISATION_UNTRACK_KEEP}
            </Button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
